import { toast } from '@/components/ui/toaster'
import {
  SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY,
  TokenResponse,
} from '@/app/registry/service-registry/_hooks/use-service-oauth-connection'
import { useTranslate } from '@tolgee/react'
import React from 'react'
import { useServiceOAuthQueryState } from './use-service-oauth-query-state'
import { ServiceRegistryFormData } from './use-service-form-validation'
import { useEffectOnce } from 'react-use'

export type ServiceOAuthCallbackUrlParams = {
  code?: string
  state?: string
}

export type ServiceOAuthCallbackData = {
  formData: ServiceRegistryFormData
  urlParams: ServiceOAuthCallbackUrlParams
}

export type UseServiceOAuthCallbackProps = {
  handleOAuthCallback: (code: string, state: string) => Promise<TokenResponse | void>
  clearOAuthState: () => void
}

export type UseServiceOAuthCallbackResult = {
  callbackData: ServiceOAuthCallbackData
}

export function useServiceOAuthCallback({
  handleOAuthCallback,
  clearOAuthState,
}: UseServiceOAuthCallbackProps): UseServiceOAuthCallbackResult {
  const { t } = useTranslate()
  const { oauthParams, setOAuthParams, isOAuthSuccess, isOAuthError } = useServiceOAuthQueryState()
  const { code, state } = oauthParams
  const [callbackData, setCallbackData] = React.useState<ServiceOAuthCallbackData>(() => ({
    formData: {
      name: '',
      url: '',
      serviceType: '',
      key: '',
    },
    urlParams: { code, state },
  }))

  const onError = React.useCallback(() => {
    clearOAuthState()
    toast.error(t('serviceRegistry.dialog.toast.connectionFailed'))
  }, [])

  useEffectOnce(() => {
    if (isOAuthSuccess || isOAuthError) {
      const savedFormDataJson = sessionStorage.getItem(
        SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY,
      )

      if (savedFormDataJson) {
        try {
          const parsedFormData = JSON.parse(savedFormDataJson) || {}
          setCallbackData((prev) => ({
            ...prev,
            formData: parsedFormData,
          }))
        } catch (parseError) {
          onError()
          console.error('Failed to parse saved form data:', parseError)
          return
        }
        if (isOAuthError) {
          onError()
        }
        if (isOAuthSuccess) {
          handleOAuthCallback(code, state)
        }
        sessionStorage.removeItem(SERVICE_REGISTRY_FORM_DATA_SESSION_STORAGE_KEY)
      } else {
        onError()
      }

      setOAuthParams({
        code: '',
        state: '',
        error: '',
        error_description: '',
        serviceId: '',
      })
    }
  })

  return { callbackData }
}
